import React, { Suspense, lazy, useState } from "react";
import { Route, BrowserRouter, Redirect, Link, withRouter } from "react-router-dom";
import { useLocalization } from './hooks/useLocalization';
import { QueryClient, QueryClientProvider } from "react-query";
import { StoreProvider } from "./store/store";
import "dotenv/config";
import "./App.scss";
import Loader from "./components/Loader";
import Nav from "./components/Nav";
import AdminNav from "./components/Admin/AdminNav";
import Login from "./components/Login";
import Carvibes from "./components/Carvibes";
import Explore from "./components/Explore";
import Settings from "./components/Settings";
import Feed from "./components/Feed";
import Chat from "./components/Chat";

import ProfileEdit from "./components/ProfileEdit";
import CarEdit from "./components/CarEdit";
import CarListing from "./components/CarListing";
import AdminLogin from "./components/Admin/Login";
import UserList from "./components/Admin/UserList";
import UserDetail from "./components/Admin/UserDetail";
import NewUserHistory from "./components/Admin/NewUserHistory";
import SendNotification from "./components/Admin/SendNotification";
import ManagementHistory from "./components/Admin/ManageHistory";
import { useSplashAnimation } from "./hooks/useSplashAnimation";
import SurveyBadge from "./components/SurveyBadge";
import CompanyPage from './components/Company';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';

const Home = lazy(() => import("./components/Home"));
const AdminHome = lazy(() => import("./components/Admin/Home"));
const Profile = lazy(() => import("./components/Profile"));
const Post = lazy(() => import("./components/Post"));

const HIDE_NAV_PAGES = [
  /^\/settings\/[0-9a-fA-F]+\/profile-edit$/,
  /^\/settings\/[0-9a-fA-F]+\/cars\/form$/,
  /^\/admin-login/,
  /^\/admin\/signup/,
];

const LOGIN_SCREEN_TEXT = [
  "あなたの愛車でココロ踊る体験を。",
  `現在、世界中のクルマ好き XXX 人 が全力応援中!!`,
  `現在、世界中のクルマ XXX 台 が全力稼働中!!`,
  "ログインして始めましょう！",
  "Googleアカウントでログイン/登録を行います。",
  "認証できない場合はお問合せください。",
  "運営会社",
  "利用規約",
  "プライバシーポリシー",
  "お問合せ",
  "運営会社",
];

const DefaultAdminContainer = withRouter(({ history }) => {
  const hideNav = HIDE_NAV_PAGES.some((regExp) =>
    history.location.pathname.match(regExp)
  );
  return (
    <div className="body-wrap">
      <nav
        className="header"
        style={{
          display: hideNav ? "none" : "block",
        }}
      >
        <AdminNav />
      </nav>
      <main className="main">
        <div
          className={"middle-section ms-width"}
          style={{
            backgroundColor: "white",
          }}
        >
          <Route path="/admin" exact>
            <AdminHome />
          </Route>
          <Route path="/admin-login" exact>
            <AdminLogin />
          </Route>
          <Route path="/admin/user-list" exact>
            <UserList />
          </Route>
          <Route path="/admin/user-list/:userid" exact>
            <UserDetail />
          </Route>
          <Route path="/admin/new-user-history" exact>
            <NewUserHistory />
          </Route>
          <Route path="/admin/send-notification" exact>
            <SendNotification />
          </Route>
          <Route path="/admin/manage-history" exact>
            <ManagementHistory />
          </Route>
        </div>
      </main>
    </div>
  );
});

const DefaultContainer = withRouter(({ history }) => {
  const { translatedTexts, countryCode, onSelectCountryCode } =
    useLocalization(LOGIN_SCREEN_TEXT);


  const hideNav = HIDE_NAV_PAGES.some((regExp) =>
    history.location.pathname.match(regExp)
  );

  if (history.location.pathname.includes("/login")) {
    return (
      <Route path="/login" exact>
        <Login />
      </Route>
    );
  }

  if (history.location.pathname.includes("/admin")) {
    return <Route component={DefaultAdminContainer} />;
  }

  if (history.location.pathname.includes("/company")) {
    return (
      <Route path="/company" exact>
        <CompanyPage />
      </Route>
    );
  }

  if (history.location.pathname.includes("/privacy-policy")) {
    return (
      <Route path="/privacy-policy" exact>
        <PrivacyPolicy />
      </Route>
    );
  }

  if (history.location.pathname.includes("/terms-of-use")) {
    return (
      <Route path="/terms-of-use" exact>
        <TermsOfUse />
      </Route>
    );
  }

  return (
    <div >
      <div className="body-wrap">
        <nav
          className="header"
          style={{
            display: hideNav ? "none" : "block",
          }}
        >
          <Nav />
        </nav>
        <main className="main">
          <div
            className={
              history.location.pathname.slice(0, 9) !== "/messages"
                ? "middle-section ms-width"
                : "middle-section"
            }
          >
            <SurveyBadge />
            <Route path="/" exact>
              <Redirect to="/home" />
            </Route>
            <Route path="/home" exact>
              <Home />
            </Route>

            <Route path="/feed" exact>
              <Feed />
            </Route>

            <Route path="/chat" exact>
              <Chat />
            </Route>
            <Route path="/profile/:userid" exact>
              <Profile />
            </Route>
            <Route path="/post/:postId" exact>
              <Post />
            </Route>
            <Route path="/carvibes/:userid/:id" exact>
              <Carvibes />
            </Route>
            <Route path="/explore" exact>
              <Explore />
            </Route>
            <Route path="/settings/:userid" exact>
              <Settings />
            </Route>
            <Route path="/settings/:userid/profile-edit" exact>
              <ProfileEdit />
            </Route>
            <Route path="/settings/:userid/cars/form" exact>
              <CarEdit />
            </Route>
            <Route path="/settings/:userid/cars" exact>
              <CarListing />
            </Route>
          </div>
        </main>
      </div>
      <footer className="main-fixed-footer">
        <div className={"container4"}>
          <div className={"container5"}>
            <span className="copyright">
              <Link to={`/terms-of-use`}>
                {translatedTexts[7]}
              </Link>
            </span>
            <span className="copyright">
              <Link to={`/privacy-policy`}>
                {translatedTexts[8]}
              </Link>
            </span>
            <span className="copyright">
              <a
                href="https://docs.google.com/forms/d/1v91vwEmFwN0idylrob7X4jumzUq_FWKIGL76HIe_tzs/formrestricted"
                target="_blank"
                rel="noopener noreferrer"
              >
                {translatedTexts[9]}
              </a>
            </span>

            <span className="copyright">
              <Link to={`/company`}>
                {translatedTexts[10]}
              </Link>

            </span>
          </div>
        </div>
        <span className="copyright">
          COPYRIGHT 2024-2025 T&amp;F ASSET MANAGEMENT INC. ALL RIGHTS RESERVED.
        </span>
      </footer>
    </div>
  );
});

function App() {
  useSplashAnimation();
  const [queryClient] = useState(() => new QueryClient());

  return (
    <div className="dark-mode">
      <QueryClientProvider client={queryClient}>
        <StoreProvider>
          <BrowserRouter>
            <Suspense fallback={<Loader />}>
              <Route path="/" component={DefaultContainer} />
              <ToastContainer />
            </Suspense>
          </BrowserRouter>
        </StoreProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
